import { useEffect, useState } from 'react'

export interface ClockValue {
  /** e.g. "12:04 AM" */
  time: string
  /** e.g. "PDT" — shown so the reading is never ambiguous. */
  zone: string
  /** e.g. "Tuesday, July 29, 2026" */
  date: string
}

function format(now: Date): ClockValue {
  const time = now.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  const date = now.toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  })
  // Pull just the abbreviation out of a short timeZoneName format.
  const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(now)
  const zone = parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
  return { time, zone, date }
}

/**
 * Browser-local clock, with the timezone abbreviation displayed.
 *
 * Local rather than a configured warehouse timezone, by decision — but the
 * abbreviation is not decoration: "Age" columns, 24-hour windows and day
 * boundaries all inherit this zone, so the reader has to be able to see which
 * one they are looking at.
 *
 * Ticks on the minute boundary rather than every second: the display has
 * minute resolution, so a 1s interval would re-render 59 times for nothing.
 */
export function useClock(): ClockValue {
  const [value, setValue] = useState(() => format(new Date()))

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    const scheduleNextTick = () => {
      const now = new Date()
      setValue(format(now))
      const msToNextMinute = 60_000 - (now.getSeconds() * 1000 + now.getMilliseconds())
      timer = setTimeout(scheduleNextTick, msToNextMinute)
    }

    scheduleNextTick()
    return () => clearTimeout(timer)
  }, [])

  return value
}
